import { Controller } from "stimulus"

export default class extends Controller {
  connect() {
    // Find the checkbox inside this element and attach event listener
    const checkbox = this.element.querySelector('input[type="checkbox"]')
    if (checkbox) {
      checkbox.addEventListener('change', (event) => this.toggleDeactivate(event))
    }
  }

  toggleDeactivate(event) {
    // Get the checkbox/toggle element
    const checkbox = event.target

    // If toggling ON (activating), allow it
    if (checkbox.checked) {
      return
    }

    // If toggling OFF (deactivating), prevent and show modal
    event.preventDefault()
    checkbox.checked = true

    // Get organization data from element's data attributes
    const organizationId = this.element.dataset.organizationId
    const organizationName = this.element.dataset.organizationName
    const userCount = this.element.dataset.userCount

    // Update modal with organization info
    const modal = document.querySelector("#deactivateModal")
    if (modal) {
      // Store the organization name on the modal for validation
      modal.dataset.organizationName = organizationName
      modal.dataset.organizationId = organizationId

      modal.querySelector("#organizationNameDisplay").textContent = organizationName
      modal.querySelector("#organizationUserCountDisplay").textContent = userCount

      // Reset form
      const confirmationInput = modal.querySelector("#confirmationInput")
      confirmationInput.value = ""
      modal.querySelector("#confirmationHint").classList.add("d-none")
      modal.querySelector("#submitButton").disabled = true
      modal.querySelector("#errorMessage").classList.add("d-none")

      // Attach input event listener for validation (since modal is outside controller scope)
      confirmationInput.removeEventListener('input', this.validateInputHandler)
      this.validateInputHandler = (e) => this.validateInput(e)
      confirmationInput.addEventListener('input', this.validateInputHandler)

      // Attach submit button listener
      const submitButton = modal.querySelector("#submitButton")
      submitButton.removeEventListener('click', this.submitHandler)
      this.submitHandler = (e) => this.submit(e)
      submitButton.addEventListener('click', this.submitHandler)

      // Attach cancel button listener
      const cancelButton = modal.querySelector("#cancelButton")
      cancelButton.removeEventListener('click', this.cancelHandler)
      this.cancelHandler = (e) => this.cancel(e)
      cancelButton.addEventListener('click', this.cancelHandler)

      // Show modal
      const bootstrapModal = new bootstrap.Modal(modal)
      bootstrapModal.show()

      // Store checkbox reference
      this.checkbox = checkbox
      
      // Focus the input for better UX
      setTimeout(() => confirmationInput.focus(), 100)
    } else {
      console.error("Modal not found!")
    }
  }

  validateInput(event) {
    const inputValue = event.target.value.trim()
    const modal = document.querySelector("#deactivateModal")
    const organizationName = modal?.dataset?.organizationName || ""
    const matches = inputValue === organizationName

    const submitButton = modal?.querySelector("#submitButton")
    const hint = modal?.querySelector("#confirmationHint")

    if (submitButton) {
      submitButton.disabled = !matches
    }

    if (hint) {
      if (inputValue.length > 0 && !matches) {
        hint.classList.remove("d-none")
      } else {
        hint.classList.add("d-none")
      }
    }
  }

  submit(event) {
    event.preventDefault()

    const modal = document.querySelector("#deactivateModal")
    const organizationName = modal.dataset.organizationName
    const organizationId = modal.dataset.organizationId

    // Hide the first modal
    const bootstrapModal = bootstrap.Modal.getInstance(modal)
    bootstrapModal.hide()

    // Show the final confirmation modal
    const confirmationModal = document.querySelector("#deactivateConfirmationModal")
    if (confirmationModal) {
      // Set organization name in confirmation modal
      confirmationModal.querySelector("#confirmOrgName").textContent = organizationName
      confirmationModal.dataset.organizationId = organizationId

      // Reset preserve credentials checkbox
      const preserveCheckbox = confirmationModal.querySelector("#preserveCredentialsCheckbox")
      if (preserveCheckbox) {
        preserveCheckbox.checked = false
      }

      // Attach event listeners to confirmation modal buttons
      const proceedButton = confirmationModal.querySelector("#proceedConfirmationButton")
      const cancelButton = confirmationModal.querySelector("#cancelConfirmationButton")

      proceedButton.removeEventListener('click', this.proceedHandler)
      this.proceedHandler = (e) => this.proceedDeactivation(e)
      proceedButton.addEventListener('click', this.proceedHandler)

      cancelButton.removeEventListener('click', this.cancelConfirmationHandler)
      this.cancelConfirmationHandler = (e) => this.cancelConfirmation(e)
      cancelButton.addEventListener('click', this.cancelConfirmationHandler)

      // Show confirmation modal
      const bootstrapConfirmationModal = new bootstrap.Modal(confirmationModal)
      bootstrapConfirmationModal.show()
    } else {
      console.error("Confirmation modal not found")
    }
  }

  async proceedDeactivation(event) {
    event.preventDefault()

    const confirmationModal = document.querySelector("#deactivateConfirmationModal")
    const organizationId = confirmationModal.dataset.organizationId
    const proceedButton = confirmationModal.querySelector("#proceedConfirmationButton")

    // Show loading state
    proceedButton.disabled = true
    const spinner = document.createElement('span')
    spinner.className = 'spinner-border spinner-border-sm me-2'
    spinner.setAttribute('role', 'status')
    spinner.setAttribute('aria-hidden', 'true')
    proceedButton.insertBefore(spinner, proceedButton.firstChild)

    try {
      // Send POST request to update_enabled_states endpoint
      const formData = new FormData()
      formData.append(`organizations[${organizationId}][enabled]`, '0')

      const preserveCheckbox = confirmationModal.querySelector("#preserveCredentialsCheckbox")
      if (preserveCheckbox && preserveCheckbox.checked) {
        formData.append(`organizations[${organizationId}][preserve_credentials]`, '1')
      }

      const response = await fetch(
        `/admin/organizations/update_enabled_states`,
        {
          method: "POST",
          body: formData,
          headers: {
            "X-CSRF-Token": this.getCsrfToken(),
          },
          credentials: "same-origin",
        }
      )

      if (response.ok) {
        // Close modal and reload page
        const bootstrapConfirmationModal = bootstrap.Modal.getInstance(confirmationModal)
        bootstrapConfirmationModal.hide()

        setTimeout(() => {
          window.location.reload()
        }, 500)
      } else {
        console.error("Failed to deactivate organization")
        spinner.remove()
        proceedButton.disabled = false
        alert("Failed to deactivate organization. Please try again.")
      }
    } catch (error) {
      console.error("Error deactivating organization:", error)
      spinner.remove()
      proceedButton.disabled = false
      alert("An error occurred while deactivating the organization")
    }
  }

  cancelConfirmation(event) {
    event.preventDefault()

    const confirmationModal = document.querySelector("#deactivateConfirmationModal")
    const bootstrapConfirmationModal = bootstrap.Modal.getInstance(confirmationModal)
    bootstrapConfirmationModal.hide()

    // Show the original modal again
    const modal = document.querySelector("#deactivateModal")
    const bootstrapModal = new bootstrap.Modal(modal)
    bootstrapModal.show()
  }

  cancel(event) {
    event.preventDefault()
    const modal = document.querySelector("#deactivateModal")
    const bootstrapModal = bootstrap.Modal.getInstance(modal)
    if (bootstrapModal) {
      bootstrapModal.hide()
    }

    // Revert toggle
    if (this.checkbox) {
      this.checkbox.checked = true
    }
  }

  getCsrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || ""
  }
}
